// 🔬 B251 (AI_115) — CAMPOS-CHAVE do mapa de completude.
// ─────────────────────────────────────────────────────────────────────────────
// Subconjunto CURADO do catálogo de pesquisa: o que um prontuário de
// hepatologia/transplante deveria ter preenchido. Os rótulos vêm do catálogo
// (fonte única — não duplicar); o `grupo` é específico da visão de completude
// (mais legível aqui que os grupos da pesquisa).
//
// IMPORTANTE: avaliar sempre numa COORTE filtrada. Alguns campos só fazem
// sentido em certos grupos — p.ex. MELD/Child-Pugh em pré-Tx — então o médico
// filtra a coorte antes de ler o mapa (a UI deixa isso claro).
import { CAMPO_POR_CHAVE } from "./catalogoCampos";
import { CampoCompletude } from "../services/completude";

// (chave do catálogo, grupo na visão de completude). Ordem = ordem de exibição.
const DEFINICAO: { chave: string; grupo: string }[] = [
  // Identificação & demografia
  { chave: "idade", grupo: "Identificação & demografia" },
  { chave: "sexo", grupo: "Identificação & demografia" },
  { chave: "tipoSanguineo", grupo: "Identificação & demografia" },
  // Diagnóstico & gravidade
  { chave: "etiologia", grupo: "Diagnóstico & gravidade" },
  { chave: "meld", grupo: "Diagnóstico & gravidade" },
  { chave: "childPugh", grupo: "Diagnóstico & gravidade" },
  // Avaliação clínica — descompensação (B251-FU1, AI_116). Booleanos da aba
  // Avaliação Clínica: o carregador coage "nunca avaliado" a null (faltante) e
  // preserva true/false (avaliado = presente). Dirige o preenchimento do checklist
  // de descompensação. Faz sentido em coorte de cirrótico — ver aviso de coorte
  // coerente na UI. NB: paracentese e ligaduraElastica são PROCEDIMENTOS
  // (condicionais a ascite/varizes); seu "faltando" é mais ruidoso por natureza.
  { chave: "ascite", grupo: "Avaliação clínica (descompensação)" },
  { chave: "paracentese", grupo: "Avaliação clínica (descompensação)" },
  { chave: "hidrotorax", grupo: "Avaliação clínica (descompensação)" },
  { chave: "encefalopatia", grupo: "Avaliação clínica (descompensação)" },
  { chave: "varizesEsofago", grupo: "Avaliação clínica (descompensação)" },
  { chave: "varizesGastrica", grupo: "Avaliação clínica (descompensação)" },
  { chave: "hdaPrevia", grupo: "Avaliação clínica (descompensação)" },
  { chave: "ligaduraElastica", grupo: "Avaliação clínica (descompensação)" },
  { chave: "pbe", grupo: "Avaliação clínica (descompensação)" },
  { chave: "empiemaBacterianoEspontaneo", grupo: "Avaliação clínica (descompensação)" },
  { chave: "colangiteRepeticao", grupo: "Avaliação clínica (descompensação)" },
  { chave: "shr", grupo: "Avaliação clínica (descompensação)" },
  { chave: "dialise", grupo: "Avaliação clínica (descompensação)" },
  { chave: "hipertensaoPortopulmonar", grupo: "Avaliação clínica (descompensação)" },
  { chave: "shdHepatopulmonar", grupo: "Avaliação clínica (descompensação)" },
  { chave: "pruridoIntratavel", grupo: "Avaliação clínica (descompensação)" },
  { chave: "insuficienciaHepaticaAguda", grupo: "Avaliação clínica (descompensação)" },
  { chave: "aclf", grupo: "Avaliação clínica (descompensação)" },
  { chave: "tips", grupo: "Avaliação clínica (descompensação)" },
  { chave: "sarcopenia", grupo: "Avaliação clínica (descompensação)" },
  // Laboratório essencial (insumos do MELD + hemograma básico)
  { chave: "creatinina", grupo: "Laboratório essencial" },
  { chave: "bilirrubinaTotal", grupo: "Laboratório essencial" },
  { chave: "inr", grupo: "Laboratório essencial" },
  { chave: "albumina", grupo: "Laboratório essencial" },
  { chave: "sodio", grupo: "Laboratório essencial" },
  { chave: "plaquetas", grupo: "Laboratório essencial" },
  { chave: "hemoglobina", grupo: "Laboratório essencial" },
  // Sorologias de triagem pré-Tx
  { chave: "hbsag", grupo: "Sorologias de triagem" },
  { chave: "antiHCV", grupo: "Sorologias de triagem" },
  { chave: "antiHIV", grupo: "Sorologias de triagem" },
  // Marcador de rastreio de CHC
  { chave: "afp", grupo: "Marcadores" },
  // Hepatocarcinoma (CHC) — estadiamento estruturado (B251-FU1, AI_116). Só faz
  // sentido em coorte COM CHC (filtrar CHC = Sim antes); fora dela, "faltando" é
  // esperado (paciente sem CHC não tem estadiamento) — ver aviso de coorte coerente.
  { chave: "hccMilao", grupo: "Hepatocarcinoma (CHC)" },
  { chave: "hccBCLC", grupo: "Hepatocarcinoma (CHC)" },
  { chave: "hccTerapiaLocorregional", grupo: "Hepatocarcinoma (CHC)" },

  // Doador vivo de fígado (B351, AI_152). Mesma ressalva dos campos de CHC: só
  // fazem sentido na coorte de DOADORES — filtrar "Grupo = Doador de fígado"
  // antes de ler. Fora dela, "faltando" é o esperado, não uma lacuna de registro.
  { chave: "doadorEnxerto", grupo: "Doador vivo" },
  { chave: "doadorGRWR", grupo: "Doador vivo" },
  { chave: "doadorRemanescente", grupo: "Doador vivo" },
  { chave: "doadorEsteatose", grupo: "Doador vivo" },
  { chave: "doadorVariacaoArterial", grupo: "Doador vivo" },
  { chave: "doadorVariacaoPortal", grupo: "Doador vivo" },
  { chave: "doadorVariacaoBiliar", grupo: "Doador vivo" },
  { chave: "doadorPsicossocial", grupo: "Doador vivo" },
  { chave: "doadorReceptorVinculado", grupo: "Doador vivo" },
];

export const CAMPOS_COMPLETUDE: CampoCompletude[] = DEFINICAO.map((d) => ({
  chave: d.chave,
  label: CAMPO_POR_CHAVE[d.chave]?.label ?? d.chave,
  grupo: d.grupo,
}));
